// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Stack, Typography, Grid2 } from '@mui/material';
import { useMemo, useState } from 'react';
// Components, Layouts, Pages
import { BaseInputSearch, BaseLabel, Loading } from '~/components/common';
import { ItemReport } from '~/components/module/reports';
// Others
import { listDataReport } from '../helper';
import { DEFAULT_DELAY_TIME, EMPTY_STRING } from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { REPORT_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './ReportDashboard.module.scss';

const cx = classNames.bind(styles);

const ReportDashboard = () => {
  //#region Destructuring Props
  const { canViewReports } = usePermissions(REPORT_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/reports');
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [searchValue, setSearchValue] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce<string>(searchValue.trim(), DEFAULT_DELAY_TIME);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const filteredReports =
    useMemo(() => {
      try {
        setIsLoading(true);
        const reports = listDataReport(t);
        if (!debounceSearchKey) return reports;

        const lowerSearch = debounceSearchKey.toLowerCase();
        return reports.filter((item) => item.title.toLowerCase().includes(lowerSearch));
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }, [debounceSearchKey, t]) || [];
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleChangeSearch = (value: string) => {
    setSearchValue(value);
  };
  //#endregion Handle Function

  return (
    <Stack id='reportPage' className={cx('container')} spacing={6}>
      <Stack direction={'column'} spacing={8}>
        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_dashboard_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_dashboard_desc')}</Typography>
        </Stack>

        <BaseInputSearch
          width={384}
          height={46}
          borderRadius={20}
          typeStyle='outlined'
          value={searchValue}
          placeholder={t('reports_dashboard_search')}
          onChange={handleChangeSearch}
        />
      </Stack>

      <Grid2 container spacing={6}>
        {canViewReports && filteredReports.length ? (
          filteredReports.map((item, index) => (
            <Grid2 size={3} key={index}>
              <ItemReport item={item} />
            </Grid2>
          ))
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        )}
      </Grid2>

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ReportDashboard;
